import React, {useMemo, useRef, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    LinearProgress,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditableText from "./EditableText";
import SectionTaskCard from "./SectionTaskCard";
import SeriesCard from "./SeriesCard";
import {groupSectionEntries} from "../../utilities/groupSeries";
import {useBoardTasks, useCreateTask} from "../../data/tasks";
import type {TaskFocus} from "./Board";
import AddIcon from "@mui/icons-material/Add";
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import {NewTaskDialog} from "./NewTaskDialog";
import {BoardRole, canEditBoard} from "../../types/task";
import {useBoard, useDeleteSection, useRenameSection} from '../../data/board';

type TaskColumnProps = {
    id: string,
    title: string;
    boardId: string;
    viewerRole?: BoardRole;
    focus?: TaskFocus;
}


function Section({id, title, boardId, viewerRole, focus}: TaskColumnProps) {
    const canEdit = canEditBoard(viewerRole);
    const [description, setDescription] = useState('');
    const [newTaskTitle, setNewTaskTitle] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [newTaskDialogOpen, setNewTaskDialogOpen] = useState(false);
    const bodyRef = useRef<HTMLDivElement>(null);
    // The board page has already loaded the board; a section only reads it from the cache.
    const {data: board, isLoading, isError} = useBoard(boardId, {fetch: false});

    // The board's task list tells us which tasks belong to a recurring series, so a series can be
    // shown as one card instead of one card per occurrence.
    const {data: boardTasks, isLoading: isLoadingTasks} = useBoardTasks(boardId);

    // This section's task ids in board order, with each recurring series collapsed into one entry.
    const entries = useMemo(
        () => groupSectionEntries(board?.sectionsById?.[id]?.taskIds ?? [], boardTasks ?? []),
        [board, id, boardTasks]
    );

    const {mutate: createTask} = useCreateTask(boardId);

    const {mutate: renameSection} = useRenameSection(boardId);
    const {mutate: deleteSection} = useDeleteSection(boardId);
    const updateSectionTitle = (title: string) => renameSection({sectionId: id, title});

    function submitNewTask() {
        const title = newTaskTitle.trim();
        if (!title) return;

        createTask({sectionId: id, title, description}, {
            onSuccess: () => {
                setDescription('');
                setNewTaskTitle('');

                // The new card lands at the bottom of the column; bring it into view.
                setTimeout(() => bodyRef.current?.scrollTo({top: bodyRef.current.scrollHeight, behavior: 'smooth'}), 0);
            },
        });
    }

    function cancelAddingTask() {
        setIsAdding(false);
        setNewTaskTitle('');
    }

    const sectionTasks = (boardTasks ?? []).filter((t: any) => t.sectionId === id);
    const doneCount = sectionTasks.filter((t: any) => t.isCompleted).length;

    if (isLoading) return <p>Loading…</p>;
    if (isError) return <p>Error</p>;

    return (
        <Paper
            variant="outlined"
            sx={{
                // One fixed-width column: a header, a body that scrolls on its own, and an add-task footer.
                flex: '0 0 min(340px, 100%)',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 'max(480px, calc(100vh - 280px))',
                overflow: 'hidden',
                scrollSnapAlign: 'start',
                borderRadius: 2,
            }}
        >
            <Box sx={{px: 2, pt: 1.5, pb: 1.5, borderBottom: 1, borderColor: 'divider'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Box sx={{minWidth: 0}}>
                        <EditableText
                            value={title}
                            onChange={(newTitle) => updateSectionTitle(newTitle)}
                            disabled={!canEdit}
                            variant="subtitle1"
                            sx={{fontWeight: 600}}
                        />
                    </Box>
                    <Chip label={sectionTasks.length} size="small" variant="outlined" />
                    <Box sx={{flexGrow: 1}}/>
                    {canEdit && (
                        <Tooltip title="Delete section">
                            <IconButton
                                size="small"
                                aria-label="Delete section"
                                onClick={() => setConfirmingDelete(true)}
                                sx={{color: 'text.secondary', opacity: 0.7, '&:hover': {color: 'error.main', opacity: 1}}}
                            >
                                <DeleteIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                {sectionTasks.length > 0 && (
                    <Box sx={{mt: 1}}>
                        <LinearProgress
                            variant="determinate"
                            value={(doneCount / sectionTasks.length) * 100}
                            color="success"
                            sx={{height: 4, borderRadius: 2}}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {doneCount} of {sectionTasks.length} done
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box ref={bodyRef} sx={{flex: 1, overflowY: 'auto', p: 1.5, bgcolor: '#F1F4EF'}}>
                <Stack spacing={1.25}>
                    {isLoadingTasks && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
                    {!isLoadingTasks && board && entries.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{py: 2, textAlign: 'center'}}>
                            No tasks yet
                        </Typography>
                    )}
                    {!isLoadingTasks && board && entries.map((entry) => entry.kind === 'series' ? (
                        <SeriesCard
                            key={`series-${entry.id}`}
                            tasks={entry.tasks}
                            viewerRole={viewerRole}
                            focus={focus?.seriesId === entry.id ? focus : undefined}
                        />
                    ) : (
                        <SectionTaskCard key={entry.id} id={entry.id} boardId={boardId} viewerRole={viewerRole} fluid/>
                    ))}
                </Stack>
            </Box>

            {canEdit && (
                <Box sx={{p: 1, borderTop: 1, borderColor: 'divider'}}>
                    {isAdding ? (
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            placeholder="Task name…"
                            value={newTaskTitle}
                            onChange={(e) => setNewTaskTitle(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') submitNewTask();
                                if (e.key === 'Escape') cancelAddingTask();
                            }}
                            onBlur={() => {
                                if (!newTaskTitle.trim()) cancelAddingTask();
                            }}
                            helperText="Enter to add, Esc to cancel"
                        />
                    ) : (
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                            <Button
                                fullWidth
                                startIcon={<AddIcon/>}
                                onClick={() => setIsAdding(true)}
                                sx={{justifyContent: 'flex-start', color: 'text.secondary'}}
                            >
                                Add task
                            </Button>
                            {/* The same dialog as the week view's "New task": a date and time, a description and a repeat. */}
                            <Tooltip title="Add a task with a date, description or repeat">
                                <IconButton
                                    aria-label="Add a task with a date, description or repeat"
                                    onClick={() => setNewTaskDialogOpen(true)}
                                    sx={{color: 'text.secondary'}}
                                >
                                    <EventRepeatIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            )}

            {newTaskDialogOpen && (
                <NewTaskDialog
                    boardId={boardId}
                    sections={(board?.sectionOrder ?? []).map((sectionId) => ({id: sectionId, title: board?.sectionsById?.[sectionId]?.title || 'Untitled section'}))}
                    defaultSectionId={id}
                    onClose={() => setNewTaskDialogOpen(false)}
                />
            )}

            <Dialog open={confirmingDelete} onClose={() => setConfirmingDelete(false)}>
                <DialogTitle>Delete "{title}"?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {sectionTasks.length > 0
                            ? `This permanently deletes the section and its ${sectionTasks.length} ${sectionTasks.length === 1 ? 'task' : 'tasks'}, including any archived ones.`
                            : 'This permanently deletes the section.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmingDelete(false)}>Cancel</Button>
                    <Button color="error" onClick={() => { setConfirmingDelete(false); deleteSection(id); }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}

export default Section;
